<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { navFor } from '@/router/navigation';

const props = withDefaults(
  defineProps<{
    orientation?: 'horizontal' | 'vertical';
    /** Icon-only rail: render the item's icon and hide its caption text. */
    collapsed?: boolean;
  }>(),
  { orientation: 'horizontal', collapsed: false },
);

const auth = useAuthStore();
const route = useRoute();

const visibleItems = computed(() => {
  if (!auth.isLoggedIn) return [];
  return navFor(auth.emslUserType).filter((item) => {
    if (item.hidden) return false;
    if (item.requiresMultiOrg && !auth.organizationCriteria) return false;
    return true;
  });
});

// pageSignature mirrors the route name, so the current page's link is the one
// whose signature matches the active route.
function isActive(pageSignature: string): boolean {
  return route.name === pageSignature;
}
</script>

<template>
  <nav
    v-if="visibleItems.length"
    :class="[
      'main-nav',
      `main-nav--${props.orientation}`,
      { 'main-nav--collapsed': props.collapsed },
    ]"
  >
    <ul class="main-nav__list">
      <li v-for="item in visibleItems" :key="item.pageSignature" class="main-nav__item">
        <a
          :href="item.url"
          :title="item.caption"
          :class="[
            `main-nav__link main-nav__link--${item.pageSignature}`,
            { 'main-nav__link--active': isActive(item.pageSignature) },
          ]"
          :aria-current="isActive(item.pageSignature) ? 'page' : undefined"
        >
          <i
            v-if="props.collapsed"
            :class="['main-nav__icon', item.icon || 'pi pi-circle']"
            aria-hidden="true"
          />
          <span :class="{ 'main-nav__caption--sr': props.collapsed }">{{ item.caption }}</span>
        </a>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.main-nav__list {
  display: flex;
  gap: 1.25rem;
  list-style: none;
  margin: 0;
  padding: 0;
}
/* Horizontal (top-bar) layout: nav sits to the right, wraps if cramped. */
.main-nav--horizontal .main-nav__list {
  flex-wrap: wrap;
  justify-content: flex-end;
}
/* Vertical (left-ribbon) layout: links stack top-down, full width. */
.main-nav--vertical .main-nav__list {
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
}
.main-nav__item {
  display: flex;
  align-items: center;
}
.main-nav--vertical .main-nav__item {
  align-items: stretch;
}
.main-nav--vertical .main-nav__link {
  display: block;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border-bottom: none;
}
.main-nav--vertical .main-nav__link--active {
  border-bottom: none;
  background: #eff6ff;
}

/* Collapsed icon rail: centre the glyph, drop the caption from view (kept in
 * the DOM for screen readers; the anchor's title= provides a hover tooltip). */
.main-nav--collapsed .main-nav__link {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}
.main-nav__icon {
  font-size: 1.15rem;
  line-height: 1;
}
.main-nav__caption--sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.main-nav__link {
  color: #1f2933;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9375rem;
  white-space: nowrap;
  /* Transparent underline reserves space so the active state doesn't reflow. */
  border-bottom: 2px solid transparent;
  padding-bottom: 2px;
}
.main-nav__link:hover {
  color: #2563eb;
}
.main-nav__link--active {
  color: #2563eb;
  font-weight: 700;
  border-bottom-color: #2563eb;
}
</style>
